import { Room } from '@era/locations-sdk';
import { createReducer, on } from '@ngrx/store';
import {
  CategoryApiActions,
  CategoryGeoLocationsActions,
  CategoryListActions,
  CategorySignalRHubActions,
  CategoryViewActions,
} from './category.actions';
import { adapter, initialState, selectId, selectIdFromKeys } from './category.state';

export const categoryReducer = createReducer(
  initialState,
  // API loaded
  on(CategoryApiActions.categoriesLoaded, (state, { configurations }) => adapter.upsertMany(configurations, state)),
  // API updated
  on(CategoryApiActions.categoryUpdated, (state, { configuration }) =>
    adapter.updateOne(
      {
        id: selectId(configuration),
        changes: configuration,
      },
      state
    )
  ),
  // API updated error
  on(CategoryApiActions.categoryUpdatedError, (state, { originalConfiguration }) =>
    adapter.upsertOne(originalConfiguration, state)
  ),
  // API deleted
  on(CategoryApiActions.categoryDeleted, (state, { configuration }) =>
    adapter.removeOne(selectId(configuration), state)
  ),
  // API deleted error
  on(CategoryApiActions.categoryDeletedError, (state, { configuration }) => adapter.upsertOne(configuration, state)),
  // API added
  on(CategoryApiActions.categoryAdded, (state, { configuration }) => adapter.upsertOne(configuration, state)),
  // API added error
  on(CategoryApiActions.categoryAddedError, (state, { configuration }) =>
    adapter.removeOne(selectId(configuration), state)
  ),
  // View added
  on(CategoryViewActions.categoryAdded, (state, { entity }) => {
    return adapter.upsertOne(entity, state);
  }),
  // View updated
  on(CategoryViewActions.categoryUpdated, (state, { originalEntity, entity }) => {
    return adapter.updateOne(
      {
        id: selectId(originalEntity),
        changes: entity,
      },
      state
    );
  }),
  // View deleted
  on(CategoryViewActions.categoryDeleted, (state, { entity }) => {
    return adapter.removeOne(selectId(entity), state);
  }),
  // List updated (Location)
  on(CategoryListActions.categoryUpdated, (state, { originalEntity, entity }) => {
    return adapter.updateOne(
      {
        id: selectId(originalEntity),
        changes: entity,
      },
      state
    );
  }),
  // List added (Location)
  on(CategoryListActions.categoryAdded, (state, { entity }) => {
    return adapter.upsertOne(entity, state);
  }),
  // List deleted (Location)
  on(CategoryListActions.categoryDeleted, (state, { entity }) => {
    return adapter.removeOne(selectId(entity), state);
  }),
  // Geo locations updated (Location)
  on(CategoryGeoLocationsActions.categoryUpdated, (state, { entity }) => {
    return adapter.upsertOne(entity, state);
  }),
  // Status changed (Room check in/out)
  on(CategoryApiActions.statusChanged, (state, { categoryType, categoryId, status }) => {
    if (categoryType === 'Room') {
      const id = selectIdFromKeys(categoryType, categoryId);
      const existing = state.entities[id];
      if (existing) {
        const updatedRoom = { ...(existing.entity as Room), isCheckedIn: status };
        return adapter.updateOne(
          {
            id,
            changes: {
              entity: updatedRoom,
            },
          },
          state
        );
      }
    }
    return state;
  }),
  // SignalR Hub updated (Room status update)
  on(CategorySignalRHubActions.categoryUpdated, (state, { categoryType, categoryId, status }) => {
    if (categoryType === 'Room') {
      const id = selectIdFromKeys(categoryType, categoryId);
      const existing = state.entities[id];
      if (existing) {
        const updatedRoom = { ...(existing.entity as Room), isCheckedIn: status === 'CheckedIn' };
        return adapter.updateOne(
          {
            id,
            changes: {
              entity: updatedRoom,
            },
          },
          state
        );
      }
    }
    return state;
  })
);

const { selectIds, selectEntities, selectAll } = adapter.getSelectors();

// Internal selectors - used by category.selector.ts
export const selectCategoryIds = selectIds;
export const selectCategoryEntities = selectEntities;
export const selectAllCategories = selectAll;
